import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import { resolveSftpAuthMode, SFTPConnection } from "types/interfaces/sftp";
import type { BackendUser } from "types/interfaces/adminUsers";
import {
  useCreateSftpConnectionMutation,
  useUpdateSftpConnectionMutation,
  useDeleteSftpConnectionMutation,
  useGetSftpPublicConfigQuery,
  useLazyCheckSftpUsernameQuery,
} from "features/sftp/sftp";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useBackendUsersAutocompleteInfinite } from "features/adminUsers/useBackendUsersAutocompleteInfinite";
import {
  BackendUserLabelBlock,
  backendUserAutocompleteComponentsProps,
  BackendUserAutocompleteListbox,
  renderBackendUserAutocompleteOption,
} from "components/common/BackendUserLabelBlock";
import { formatBackendUserOptionLabel } from "utils/backendUserLabel";
import AppDialog from "components/common/AppDialog";
import CustomScrollbars from "components/CustomScrollbars";
import SftpFolderPickerDialog, {
  FolderPickerResult,
} from "./SftpFolderPickerDialog";
import KeyTypePickerDialog from "./KeyTypePickerDialog";
import SftpFolderMountsEditor from "./SftpFolderMountsEditor";
import {
  comparableFolderMountsFromConnection,
  folderMountRowAliasesValid,
  folderMountRowsFromConnection,
  folderMountsToPayload,
  formatSubfolderDisplay,
  type FolderMountRow,
  newFolderMountRow,
} from "./sftpFolderMountUtils";

type AuthMethod = "password" | "key" | "both";

function generateSecurePassword(length = 12): string {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const all = upper + lower + digits;
  const arr = new Uint32Array(length);
  let pw: string;
  do {
    crypto.getRandomValues(arr);
    pw = Array.from(arr, (v) => all[v % all.length]).join("");
  } while (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw) || !/[0-9]/.test(pw));
  return pw;
}

interface SftpConnectionPanelProps {
  connection: SFTPConnection | null;
  onClose: () => void;
  onSaved: (conn: SFTPConnection) => void;
  onDeleted: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  lockedBackendUser?: BackendUser | null;
  embeddedInDialog?: boolean;
}

export default function SftpConnectionPanel({
  connection,
  onClose,
  onSaved,
  onDeleted,
  onDirtyChange,
  onSavingChange,
  lockedBackendUser = null,
  embeddedInDialog = false,
}: SftpConnectionPanelProps) {
  const dispatch = useAppDispatch();
  const isCreateMode = connection === null;

  const [name, setName] = useState(connection?.name ?? "");
  const [username, setUsername] = useState(connection?.username ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [bucket, setBucket] = useState(connection?.bucket ?? "");
  const [folderMountRows, setFolderMountRows] = useState<FolderMountRow[]>(() =>
    folderMountRowsFromConnection(connection),
  );
  const [folderPickerRowIndex, setFolderPickerRowIndex] = useState<
    number | null
  >(null);
  const [role, setRole] = useState<"read" | "write">(
    (connection?.role as "read" | "write") ?? "read",
  );
  const [isActive, setIsActive] = useState(connection?.is_active ?? true);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(() =>
    connection ? resolveSftpAuthMode(connection) : "password",
  );
  const [publicKeyText, setPublicKeyText] = useState("");

  const [selectedUser, setSelectedUser] = useState<BackendUser | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [nameError, setNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const usernameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [keyPickerOpen, setKeyPickerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [closeDiscardOpen, setCloseDiscardOpen] = useState(false);

  const {
    options: backendUserOptions,
    loading: backendUsersLoading,
    onListboxScroll: backendUsersListboxOnScroll,
  } = useBackendUsersAutocompleteInfinite({
    skip: !!lockedBackendUser,
    page_size: 50,
    search: userSearch,
    sort_by: "name",
    sort_order: "asc",
    value: selectedUser,
  });

  const [checkUsername] = useLazyCheckSftpUsernameQuery();
  const { data: publicConfig } = useGetSftpPublicConfigQuery();
  const defaultServerId =
    publicConfig?.default_server_id ?? "s-84190f80606e405c9";
  const sftpHost = publicConfig?.public_hostname ?? "sftp.calibratehcm.com";
  const usernameCheckServerId = connection?.server_id ?? defaultServerId;

  useEffect(() => {
    if (connection) {
      setName(connection.name ?? "");
      setUsername(connection.username ?? "");
      setPassword("");
      setShowPassword(false);
      setBucket(connection.bucket ?? "");
      setFolderMountRows(folderMountRowsFromConnection(connection));
      setRole((connection.role as "read" | "write") ?? "read");
      setIsActive(connection.is_active ?? true);
      setPublicKeyText("");
      setAuthMethod(resolveSftpAuthMode(connection));
      setUsernameError("");
      setNameError("");
      setPasswordError("");
      if (connection.user_id) {
        const fromApi = connection.backend_user;
        if (fromApi && fromApi.id === connection.user_id) {
          setSelectedUser(fromApi);
        } else {
          setSelectedUser(null);
        }
      } else {
        setSelectedUser(null);
      }
    } else {
      setName("");
      setUsername("");
      setPassword("");
      setShowPassword(false);
      setBucket("");
      setFolderMountRows([newFolderMountRow()]);
      setRole("read");
      setIsActive(true);
      setAuthMethod("password");
      setPublicKeyText("");
      setSelectedUser(lockedBackendUser ?? null);
      setUsernameError("");
      setNameError("");
      setPasswordError("");
    }
  }, [
    connection?.id,
    connection?.updated_at,
    connection?.backend_user,
    lockedBackendUser?.id,
  ]);

  useEffect(() => {
    if (!connection?.user_id) return;
    if (lockedBackendUser) return;
    const fromApi = connection.backend_user;
    if (fromApi && fromApi.id === connection.user_id) return;
    const match = backendUserOptions.find((u) => u.id === connection.user_id);
    if (match) setSelectedUser(match);
  }, [
    connection?.id,
    connection?.user_id,
    connection?.backend_user,
    lockedBackendUser,
    backendUserOptions,
  ]);

  const [createConn, { isLoading: isCreating }] =
    useCreateSftpConnectionMutation();
  const [updateConn, { isLoading: isSaving }] =
    useUpdateSftpConnectionMutation();
  const [deleteConn, { isLoading: isDeleting }] =
    useDeleteSftpConnectionMutation();

  const isBusy = isCreating || isSaving || isDeleting;

  const validateUsername = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setUsernameError("");
        return;
      }
      if (trimmed.length < 3) {
        setUsernameError("Username must be at least 3 characters");
        return;
      }
      if (!/^[a-zA-Z0-9._-]+$/.test(trimmed)) {
        setUsernameError("Spaces are not permitted in usernames.");
        return;
      }
      if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current);
      usernameCheckTimer.current = setTimeout(async () => {
        try {
          const result = await checkUsername({
            username: trimmed,
            exclude_id: connection?.id,
            server_id: usernameCheckServerId,
          }).unwrap();
          setUsernameError(result.exists ? "Username already taken" : "");
        } catch {
          setUsernameError("");
        }
      }, 400);
    },
    [checkUsername, connection?.id, usernameCheckServerId],
  );

  const validatePassword = useCallback((value: string) => {
    if (!value) {
      setPasswordError("");
      return;
    }
    if (value.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (!/[a-z]/.test(value)) {
      setPasswordError("Must contain a lowercase letter");
      return;
    }
    if (!/[A-Z]/.test(value)) {
      setPasswordError("Must contain an uppercase letter");
      return;
    }
    if (!/[0-9]/.test(value)) {
      setPasswordError("Must contain a number");
      return;
    }
    setPasswordError("");
  }, []);

  const showsPassword = authMethod === "password" || authMethod === "both";
  const showsKey = authMethod === "key" || authMethod === "both";

  const isFormValid = (() => {
    if (!name.trim() || !!nameError) return false;
    if (!username.trim() || !!usernameError) return false;
    if (!selectedUser) return false;
    if (!bucket.trim()) return false;
    if (
      folderMountRows.length === 0 ||
      !folderMountRows.every((m) => m.prefix.trim())
    )
      return false;
    if (folderMountRows.length > 1) {
      const labels = folderMountRows.map((m) => m.label.trim());
      if (labels.some((lb) => !lb)) return false;
      if (new Set(labels).size !== labels.length) return false;
    }
    if (!folderMountRows.every(folderMountRowAliasesValid)) return false;
    if (isCreateMode) {
      if (showsPassword && (!password.trim() || !!passwordError)) return false;
      if (showsKey && !publicKeyText.trim()) return false;
    } else {
      if (password && !!passwordError) return false;
    }
    return true;
  })();

  const handleDeleteConfirm = async () => {
    if (!connection) return;
    try {
      await deleteConn(connection.id).unwrap();
      setDeleteConfirmOpen(false);
      setDeleteConfirmInput("");
      onDeleted();
    } catch {
      showToast("Failed to delete connection.", "error");
      setDeleteConfirmOpen(false);
    }
  };

  const handleFolderPicked = (result: FolderPickerResult) => {
    if (folderPickerRowIndex === null) return;
    setBucket(result.bucket);
    setFolderMountRows((prev) => {
      const next = [...prev];
      const row = next[folderPickerRowIndex];
      if (!row) return prev;
      next[folderPickerRowIndex] = {
        ...row,
        prefix: result.folder,
        subfolders: result.subfolders,
        subfolder_aliases: result.subfolder_aliases,
      };
      return next;
    });
    setFolderPickerRowIndex(null);
  };

  const showToast = (message: string, severity: "success" | "error") => {
    dispatch(pushNotification({ message, severity }));
  };

  const displayName =
    connection?.name || connection?.username || "New SFTP Connection";

  const mountsPayload = folderMountsToPayload(folderMountRows);
  const currentMountsKey = JSON.stringify(mountsPayload);

  const hasChanges =
    !isCreateMode &&
    connection !== null &&
    ((connection.name ?? "") !== name.trim() ||
      connection.username !== username.trim() ||
      (connection.bucket ?? "") !== bucket.trim() ||
      connection.role !== role ||
      connection.is_active !== isActive ||
      (connection.user_id ?? null) !== (selectedUser?.id ?? null) ||
      currentMountsKey !== comparableFolderMountsFromConnection(connection) ||
      !!password ||
      (showsKey && !!publicKeyText.trim()) ||
      resolveSftpAuthMode(connection) !== authMethod);

  const hasCreateDraft =
    isCreateMode &&
    Boolean(
      name.trim() ||
      username.trim() ||
      password.trim() ||
      bucket.trim() ||
      folderMountRows.some((m) => m.prefix.trim() || m.label.trim()) ||
      publicKeyText.trim() ||
      selectedUser,
    );

  const isDirty = isCreateMode ? hasCreateDraft : hasChanges;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(isCreating || isSaving || isDeleting);
  }, [isCreating, isSaving, isDeleting, onSavingChange]);

  const buildEditSaveConfirmSections = (): {
    label: string;
    updated: string[];
  }[] => {
    if (!connection) return [];
    const out: { label: string; updated: string[] }[] = [];
    const profile: string[] = [];
    if ((connection.name ?? "").trim() !== name.trim())
      profile.push("Display name");
    if (connection.username !== username.trim()) profile.push("Username");
    if ((connection.user_id ?? null) !== (selectedUser?.id ?? null)) {
      profile.push("Backend user");
    }
    if (profile.length > 0) out.push({ label: "PROFILE", updated: profile });
    const folderChanged =
      (connection.bucket ?? "") !== bucket.trim() ||
      currentMountsKey !== comparableFolderMountsFromConnection(connection);
    if (folderChanged) {
      out.push({
        label: "S3 FOLDER",
        updated: ["Bucket, folder, subfolders, or SFTP names"],
      });
    }
    const access: string[] = [];
    if (connection.role !== role) access.push("Access role");
    if (connection.is_active !== isActive) access.push("Status");
    if (resolveSftpAuthMode(connection) !== authMethod) {
      access.push("Authentication method");
    }
    if (access.length > 0) out.push({ label: "ACCESS", updated: access });
    const cred: string[] = [];
    if (password.trim()) cred.push("Password");
    if (showsKey && publicKeyText.trim()) cred.push("New public key (append)");
    if (cred.length > 0) out.push({ label: "CREDENTIALS", updated: cred });
    return out;
  };

  const buildCreateConfirmSections = (): {
    label: string;
    added: string[];
  }[] => {
    const mountLines =
      bucket.trim() && mountsPayload.some((m) => m.prefix.trim())
        ? mountsPayload
            .filter((m) => m.prefix.trim())
            .map((m) => {
              const path = `${bucket.trim()}/${m.prefix}`;
              const sub =
                m.subfolders.length > 0
                  ? ` — subfolders: ${m.subfolders
                      .map((s) =>
                        formatSubfolderDisplay(s, m.subfolder_aliases),
                      )
                      .join(", ")}`
                  : "";
              const head = m.label.trim()
                ? `${m.label.trim()} → ${path}`
                : path;
              return `${head}${sub}`;
            })
        : [];
    const added: string[] = [
      `Display name: ${name.trim() || "—"}`,
      `Username: ${username.trim() || "—"}`,
      selectedUser
        ? `Backend user: ${formatBackendUserOptionLabel(selectedUser)}`
        : "Backend user: —",
      mountLines.length > 0 ? `S3: ${mountLines.join("; ")}` : "S3 folder: —",
      `Role: ${role}`,
      `Authentication: ${authMethod}`,
    ];
    if (showsPassword) added.push("Password: set");
    if (showsKey && publicKeyText.trim()) added.push("Public key: provided");
    return [{ label: "NEW CONNECTION", added }];
  };

  const performCreate = async () => {
    if (!isFormValid) return;
    setCreateConfirmOpen(false);
    try {
      const mounts = folderMountsToPayload(folderMountRows);
      const payload: Record<string, unknown> = {
        name: name.trim(),
        username: username.trim(),
        server_id: defaultServerId,
        bucket: bucket.trim(),
        folder: mounts[0]?.prefix ?? "",
        subfolders: mounts[0]?.subfolders ?? [],
        folder_mounts: mounts,
        role,
        is_active: true,
        user_id: selectedUser?.id ?? null,
        auth_mode: authMethod,
      };
      if (showsPassword && password) payload.password = password;
      if (showsKey && publicKeyText.trim()) {
        payload.public_keys = [publicKeyText.trim()];
      }
      const created = await createConn(
        payload as unknown as Parameters<typeof createConn>[0],
      ).unwrap();
      showToast("Connection created.", "success");
      onSaved(created);
    } catch {
      showToast("Failed to create connection.", "error");
    }
  };

  const handleCreateClick = () => {
    if (!isFormValid) return;
    setCreateConfirmOpen(true);
  };

  const performSave = async () => {
    if (!connection) return;
    setSaveConfirmOpen(false);
    try {
      const mounts = folderMountsToPayload(folderMountRows);
      const body: Record<string, unknown> = {
        id: connection.id,
        name: name.trim(),
        username: username.trim(),
        bucket: bucket.trim(),
        folder: mounts[0]?.prefix ?? "",
        subfolders: mounts[0]?.subfolders ?? [],
        folder_mounts: mounts,
        role,
        is_active: isActive,
        user_id: selectedUser?.id ?? null,
        auth_mode: authMethod,
      };
      if (password) body.password = password;
      if (showsKey && publicKeyText.trim()) {
        const existing = connection.public_keys.map((k) => k.public_key);
        body.public_keys = [...existing, publicKeyText.trim()];
      }
      const updated = await updateConn(
        body as Parameters<typeof updateConn>[0],
      ).unwrap();
      showToast("Connection saved.", "success");
      onSaved(updated);
    } catch {
      showToast("Failed to save connection.", "error");
    }
  };

  const handleEditSaveClick = () => {
    if (!connection || !hasChanges) return;
    const sections = buildEditSaveConfirmSections();
    if (sections.length === 0) {
      void performSave();
      return;
    }
    setSaveConfirmOpen(true);
  };

  const handleHeaderClose = () => {
    if (isDirty) setCloseDiscardOpen(true);
    else onClose();
  };

  return (
    <Box
      sx={(muiTheme: Theme) => ({
        width: embeddedInDialog ? "100%" : { xs: "100%", sm: 420, md: 460 },
        maxWidth: "100%",
        minWidth: 0,
        flexShrink: 0,
        maxHeight: embeddedInDialog ? "min(90vh, 720px)" : undefined,
        borderLeft: embeddedInDialog
          ? "none"
          : { xs: "none", sm: `1px solid ${muiTheme.palette.divider}` },
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        overflow: "hidden",
        ...(embeddedInDialog
          ? {
              m: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: muiTheme.shadows[3],
            }
          : {}),
      })}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              bgcolor: "calibrate.primarySoft10",
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              fontSize: 14,
              color: "primary.main",
            }}
          >
            {displayName[0]?.toUpperCase()}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
              {displayName}
            </Typography>
            {!isCreateMode && connection && (
              <Typography variant="caption" color="text.secondary">
                @{connection.username}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={handleHeaderClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {lockedBackendUser ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
              >
                Backend User
              </Typography>
              <BackendUserLabelBlock user={lockedBackendUser} />
            </Box>
          ) : (
            <Autocomplete
              options={backendUserOptions}
              loading={backendUsersLoading}
              componentsProps={backendUserAutocompleteComponentsProps}
              ListboxComponent={BackendUserAutocompleteListbox}
              ListboxProps={{ onScroll: backendUsersListboxOnScroll }}
              value={selectedUser}
              onChange={(_e, v) => {
                setSelectedUser(v);
                setBucket("");
                setFolderMountRows([newFolderMountRow()]);
              }}
              getOptionLabel={formatBackendUserOptionLabel}
              renderOption={renderBackendUserAutocompleteOption}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Backend User *"
                  onChange={(e) => setUserSearch(e.target.value)}
                  error={!selectedUser && !isCreateMode && false}
                  helperText={undefined}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              )}
            />
          )}

          <TextField
            label="Display Name *"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) setNameError("");
            }}
            onBlur={() => {
              if (!name.trim()) setNameError("");
            }}
            size="small"
            fullWidth
            error={!!nameError}
            helperText={nameError || "Internal label"}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          />

          <TextField
            label="Username *"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (e.target.value.trim()) validateUsername(e.target.value);
              else setUsernameError("");
            }}
            onBlur={() => {
              if (username.trim()) validateUsername(username);
              else setUsernameError("");
            }}
            size="small"
            fullWidth
            error={!!usernameError}
            helperText={usernameError || undefined}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          />

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ mb: 0.5, display: "block" }}
            >
              Authentication Method
            </Typography>
            <Box display="flex" gap={1}>
              {(["password", "key", "both"] as const).map((m) => (
                <Chip
                  key={m}
                  label={
                    m === "both"
                      ? "Both"
                      : m === "key"
                        ? "Public Key"
                        : "Password"
                  }
                  size="small"
                  clickable
                  onClick={() => setAuthMethod(m)}
                  sx={
                    authMethod === m
                      ? {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          fontWeight: 700,
                        }
                      : { fontWeight: 600 }
                  }
                />
              ))}
            </Box>
          </Box>

          {showsPassword && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                label={
                  isCreateMode
                    ? "Password *"
                    : "New Password (leave blank to keep)"
                }
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                onBlur={() => {
                  if (isCreateMode && showsPassword && !password.trim())
                    setPasswordError("");
                }}
                size="small"
                fullWidth
                error={!!passwordError}
                helperText={passwordError || undefined}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const pw = generateSecurePassword();
                  setPassword(pw);
                  setShowPassword(true);
                  setPasswordError("");
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  mt: 0.25,
                  minWidth: 90,
                  borderRadius: 1,
                }}
              >
                Generate
              </Button>
            </Box>
          )}

          {showsKey && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label={isCreateMode ? "Public Key *" : "Public Key (add new)"}
                value={publicKeyText}
                onChange={(e) => setPublicKeyText(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder="ssh-rsa AAAA..."
                helperText="Paste an OpenSSH public key or generate a key pair below"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: 12,
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setKeyPickerOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1,
                  alignSelf: "flex-start",
                }}
              >
                Generate Key Pair
              </Button>
            </Box>
          )}

          <SftpFolderMountsEditor
            bucket={bucket}
            setBucket={setBucket}
            selectedUser={selectedUser}
            folderMountRows={folderMountRows}
            setFolderMountRows={setFolderMountRows}
            setFolderPickerRowIndex={setFolderPickerRowIndex}
          />

          {/* Role toggle */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ mb: 0.5, display: "block" }}
            >
              Access Role
            </Typography>
            <Box display="flex" gap={1}>
              {(["read", "write"] as const).map((r) => (
                <Chip
                  key={r}
                  label={r.charAt(0).toUpperCase() + r.slice(1)}
                  size="small"
                  clickable
                  onClick={() => setRole(r)}
                  sx={
                    role === r
                      ? {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          fontWeight: 700,
                        }
                      : { fontWeight: 600 }
                  }
                />
              ))}
            </Box>
          </Box>

          {/* Active toggle — only in edit mode */}
          {!isCreateMode && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ mb: 0.5, display: "block" }}
              >
                Status
              </Typography>
              <Box display="flex" gap={1}>
                {([true, false] as const).map((v) => (
                  <Chip
                    key={String(v)}
                    label={v ? "Active" : "Inactive"}
                    size="small"
                    clickable
                    onClick={() => setIsActive(v)}
                    sx={
                      isActive === v
                        ? v
                          ? {
                              bgcolor: "rgba(46,125,50,0.10)",
                              color: "success.dark",
                              fontWeight: 700,
                              border: "1.5px solid",
                              borderColor: "success.dark",
                            }
                          : {
                              bgcolor: "rgba(198, 40, 40, 0.10)",
                              color: "#c62828",
                              fontWeight: 700,
                              border: "1.5px solid",
                              borderColor: "#c62828",
                            }
                        : { fontWeight: 600 }
                    }
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </CustomScrollbars>

      <Box
        px={2}
        pb={2}
        pt={1}
        sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}
      >
        <Divider sx={{ mb: 0.5 }} />
        {isCreateMode ? (
          <Tooltip title={!isFormValid ? "Fill required fields to create" : ""}>
            <span>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCreateClick}
                disabled={!isFormValid || isBusy}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                }}
              >
                {isCreating ? "Creating..." : "Create Connection"}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <>
            {hasChanges && (
              <Typography
                variant="caption"
                fontWeight={600}
                display="block"
                sx={{ mb: 0.5, color: "#ad1457" }}
              >
                Unsaved changes
              </Typography>
            )}
            <Tooltip title={!hasChanges ? "No changes to save" : ""}>
              <span>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleEditSaveClick}
                  disabled={!hasChanges || isBusy}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1.5,
                  }}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={isBusy}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1 }}
            >
              Delete Connection
            </Button>
          </>
        )}
      </Box>

      <KeyTypePickerDialog
        open={keyPickerOpen}
        onClose={() => setKeyPickerOpen(false)}
        onGenerated={(publicKeyOpenSSH) => setPublicKeyText(publicKeyOpenSSH)}
        username={username || "sftp"}
        onError={(msg) => showToast(msg, "error")}
      />

      <SftpFolderPickerDialog
        open={folderPickerRowIndex !== null}
        onClose={() => setFolderPickerRowIndex(null)}
        onConfirm={handleFolderPicked}
        userId={selectedUser?.id}
        initialBucket={bucket}
        initialFolder={
          folderPickerRowIndex !== null
            ? (folderMountRows[folderPickerRowIndex]?.prefix ?? "")
            : ""
        }
        initialSubfolders={
          folderPickerRowIndex !== null
            ? (folderMountRows[folderPickerRowIndex]?.subfolders ?? [])
            : []
        }
        initialSubfolderAliases={
          folderPickerRowIndex !== null
            ? (folderMountRows[folderPickerRowIndex]?.subfolder_aliases ?? {})
            : {}
        }
      />

      <AppDialog
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        title="Delete SFTP Connection"
        actions={
          <>
            <Button
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteConfirmInput("");
              }}
              disabled={isDeleting}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={
                isDeleting ||
                (connection?.username ?? "").trim() !==
                  deleteConfirmInput.trim()
              }
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
        >
          Warning
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Delete the SFTP connection for{" "}
          <strong>{connection?.name || connection?.username}</strong>? This will
          remove all associated public keys and credentials.
        </Typography>
        <TextField
          label="Type username to confirm"
          placeholder={connection?.username ?? ""}
          value={deleteConfirmInput}
          onChange={(e) => setDeleteConfirmInput(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
        />
      </AppDialog>

      <AppDialog
        open={saveConfirmOpen}
        onClose={() => !isSaving && setSaveConfirmOpen(false)}
        title="Confirm Access Changes"
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => setSaveConfirmOpen(false)}
              disabled={isSaving}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void performSave()}
              disabled={isSaving}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {buildEditSaveConfirmSections().map((section, idx) => (
          <Box key={section.label}>
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              sx={{ mb: 1 }}
            >
              {section.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              letterSpacing={0.5}
              sx={{ color: "info.main", display: "block", mb: 0.5 }}
            >
              Updated
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
              {section.updated.map((item) => (
                <Typography key={item} variant="body2" component="li">
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </AppDialog>

      <AppDialog
        open={createConfirmOpen}
        onClose={() => !isCreating && setCreateConfirmOpen(false)}
        title="Confirm Access Changes"
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => setCreateConfirmOpen(false)}
              disabled={isCreating}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void performCreate()}
              disabled={isCreating}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Confirm
            </Button>
          </>
        }
      >
        {buildCreateConfirmSections().map((section, idx) => (
          <Box key={section.label}>
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              sx={{ mb: 1 }}
            >
              {section.label}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              letterSpacing={0.5}
              sx={{ color: "success.main", display: "block", mb: 0.5 }}
            >
              Added
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}>
              {section.added.map((item) => (
                <Typography key={item} variant="body2" component="li">
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </AppDialog>

      <AppDialog
        open={closeDiscardOpen}
        onClose={() => setCloseDiscardOpen(false)}
        title="Unsaved changes"
        actions={
          <>
            <Button
              onClick={() => setCloseDiscardOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Stay
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setCloseDiscardOpen(false);
                onClose();
              }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Discard and continue
            </Button>
          </>
        }
      >
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
        >
          Warning
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You have unsaved changes
          {isCreateMode ? " on this new connection" : ""}. Stay to keep editing,
          or discard them and close the panel.
        </Typography>
      </AppDialog>

    </Box>
  );
}
